/**
 * Iter-296 — Client Calendar Review Queue.
 *
 * Triage UI for events imported from a connected Google / Outlook
 * calendar. Each row shows the event, surfaces any duplicate against
 * an existing iEvntür shift, and lists overlapping iEvntür shifts as
 * scheduling conflicts. The client can:
 *
 *   • Convert → Creates a real iEvntür series + shift via the existing
 *     multi-slot pipeline. Refuses to proceed if a duplicate was
 *     detected unless the user confirms "yes I really want a duplicate".
 *   • Already Booked Elsewhere → marks as handled-off-platform.
 *   • Ignore → dismisses without converting.
 *
 * Recurring source events are pre-expanded by the provider into one
 * queue row per occurrence, so each occurrence triages independently.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Loader2, ArrowLeft, CalendarDays, MapPin, Repeat, AlertTriangle,
  CheckCircle2, XCircle, EyeOff, RefreshCw, Filter,
} from 'lucide-react';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'converted', label: 'Converted' },
  { id: 'already_booked', label: 'Already booked' },
  { id: 'ignored', label: 'Ignored' },
  { id: 'cancelled_by_source', label: 'Cancelled at source' },
];

function fmtRange(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const sameDay = s.toDateString() === e.toDateString();
  const dateStr = s.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
  const sTime = s.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const eTime = e.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `${dateStr} · ${sTime} – ${eTime}` : `${dateStr} – ${e.toLocaleDateString()}`;
}

function StatusPill({ status }) {
  const map = {
    pending: 'bg-amber-100 text-amber-800 border-amber-300',
    converted: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    already_booked: 'bg-slate-100 text-slate-700 border-slate-300',
    ignored: 'bg-slate-100 text-slate-500 border-slate-300',
    cancelled_by_source: 'bg-rose-100 text-rose-700 border-rose-300',
  };
  return (
    <Badge variant="outline" className={map[status] || ''}>
      {STATUS_TABS.find((t) => t.id === status)?.label || status}
    </Badge>
  );
}

function ConvertDialog({ open, item, onClose, onConfirm, busy }) {
  const [form, setForm] = useState({
    talent_needed: 1,
    bill_rate: 30,
    venue_name: '',
    city: '',
    state: '',
    duties: '',
    force_duplicate: false,
  });

  useEffect(() => {
    if (item) {
      setForm({
        talent_needed: 1,
        bill_rate: 30,
        venue_name: item.venue_name || '',
        city: item.city || '',
        state: item.state || '',
        duties: '',
        force_duplicate: false,
      });
    }
  }, [item]);

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle>Convert to iEvntür shift</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm font-medium text-slate-900">{item.title}</p>
          <p className="text-xs text-slate-500">
            {fmtRange(item.start_at, item.end_at)}
            {item.location ? ` · ${item.location}` : ''}
          </p>
          {item.duplicate_of_shift_id && (
            <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Looks like a duplicate of an existing iEvntür shift. Tick the
              box below to convert anyway.
            </div>
          )}
          {item.conflict_shift_ids?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Overlaps {item.conflict_shift_ids.length} existing iEvntür shift(s).
              Confirm staffing won&apos;t be double-booked before converting.
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Talent needed</Label>
              <Input
                type="number" min="1"
                value={form.talent_needed}
                onChange={(e) => setForm({ ...form, talent_needed: Number(e.target.value) })}
                data-testid="convert-talent-needed"
              />
            </div>
            <div>
              <Label className="text-xs">Bill rate ($/hr)</Label>
              <Input
                type="number" min="0" step="0.5"
                value={form.bill_rate}
                onChange={(e) => setForm({ ...form, bill_rate: Number(e.target.value) })}
                data-testid="convert-bill-rate"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Venue</Label>
              <Input
                value={form.venue_name}
                onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
                data-testid="convert-venue"
              />
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="convert-city"
              />
            </div>
          </div>

          {item.duplicate_of_shift_id && (
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={form.force_duplicate}
                onChange={(e) => setForm({ ...form, force_duplicate: e.target.checked })}
                data-testid="confirm-force-duplicate"
              />
              Yes, create this even though a duplicate was detected.
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={() => onConfirm(form)}
            disabled={busy || (item.duplicate_of_shift_id && !form.force_duplicate)}
            data-testid="confirm-convert-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Convert to shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientCalendarReviewQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/client/calendar/queue?status=${status}&limit=100`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load review queue.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const handleConvert = async (form) => {
    if (!convertTarget) return;
    setActionId(convertTarget.id);
    try {
      const res = await api.post(
        `/client/calendar/queue/${convertTarget.id}/convert`,
        form,
      );
      toast.success(`Created shift ${res.data.shift_id.slice(0, 8)}…`);
      setConvertTarget(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'duplicate_detected') {
        toast.error('Duplicate detected — confirm in the dialog to proceed.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to convert.');
      }
    } finally {
      setActionId(null);
    }
  };

  const triage = async (id, action) => {
    setActionId(id);
    try {
      await api.post(`/client/calendar/queue/${id}/${action}`, {});
      toast.success(action === 'ignore' ? 'Ignored.' : 'Marked as already booked.');
      load();
    } catch (e) {
      toast.error('Action failed.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-calendar-queue-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-slate-900 truncate">Calendar Review Queue</h1>
            <p className="text-xs text-slate-500 truncate">
              Triage events imported from your connected calendars before they become iEvntür shifts.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-queue-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setStatus(t.id)}
                className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap ${
                  status === t.id
                    ? 'border-cyan-400 bg-cyan-50 text-cyan-800 font-medium'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`queue-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-16 text-sm text-slate-500" data-testid="empty-state-no-queue">
            <Filter className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p>No events in this bucket.</p>
            <p className="text-xs mt-1">Connect a calendar and run a sync to populate.</p>
          </div>
        )}
        {!loading && items.map((item) => (
          <Card key={item.id} data-testid={`queue-item-${item.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start gap-3">
                <CalendarDays className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-sm text-slate-900 truncate">{item.title}</h3>
                    <StatusPill status={item.status} />
                    {item.is_recurring && (
                      <Badge variant="outline" className="text-[10px]">
                        <Repeat className="w-3 h-3 mr-1" />Recurring
                      </Badge>
                    )}
                    {item.locally_edited && (
                      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800 bg-amber-50">
                        Edited locally
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-600">{fmtRange(item.start_at, item.end_at)}</p>
                  {item.location && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{item.location}</span>
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-[11px] mt-1.5">
                    {item.duplicate_of_shift_id && (
                      <span className="text-rose-700 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />Duplicate of existing shift
                      </span>
                    )}
                    {item.conflict_shift_ids?.length > 0 && (
                      <span className="text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {item.conflict_shift_ids.length} scheduling conflict
                        {item.conflict_shift_ids.length === 1 ? '' : 's'}
                      </span>
                    )}
                    <span className="text-slate-400">via {item.provider}</span>
                  </div>
                </div>
              </div>
              {item.status === 'pending' && (
                <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-slate-100">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => triage(item.id, 'ignore')}
                    disabled={actionId === item.id}
                    data-testid={`ignore-btn-${item.id}`}
                  >
                    <EyeOff className="w-3.5 h-3.5 mr-1" />Ignore
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => triage(item.id, 'already-booked')}
                    disabled={actionId === item.id}
                    data-testid={`already-booked-btn-${item.id}`}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" />Already booked
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setConvertTarget(item)}
                    disabled={actionId === item.id}
                    data-testid={`convert-btn-${item.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Convert to shift
                  </Button>
                </div>
              )}
              {item.status === 'converted' && item.converted_to_shift_id && (
                <div className="pt-2 border-t border-slate-100 text-xs text-emerald-700">
                  Converted → shift {item.converted_to_shift_id.slice(0, 8)}…
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </main>

      <ConvertDialog
        open={!!convertTarget}
        item={convertTarget}
        onClose={() => setConvertTarget(null)}
        onConfirm={handleConvert}
        busy={!!actionId}
      />
    </div>
  );
}
